const normalizeCacheKeyPart = (value) => {
    if (value == null) {
        return '';
    }
    if (Array.isArray(value)) {
        return value.map(normalizeCacheKeyPart).join(',');
    }
    if (value && typeof value === 'object') {
        return JSON.stringify(
            Object.keys(value)
                .sort()
                .reduce((acc, key) => {
                    acc[key] = value[key];
                    return acc;
                }, {}),
        );
    }
    return String(value);
};

export function buildCompletedResultCacheKey(parts = []) {
    if (!Array.isArray(parts)) {
        return '';
    }
    return parts.map(normalizeCacheKeyPart).join('|');
}

export function createCompletedResultCache({ now = () => Date.now() } = {}) {
    const completedResults = new Map();
    const inFlightLoads = new Map();
    const keyVersions = new Map();
    let globalVersion = 0;

    const toKey = (key) => String(key ?? '');
    const getKeyVersion = (key) => keyVersions.get(key) ?? 0;
    const bumpKeyVersion = (key) => {
        keyVersions.set(key, getKeyVersion(key) + 1);
    };

    const purgeExpiredKey = (key) => {
        const entry = completedResults.get(key);
        if (!entry) {
            return undefined;
        }
        if (entry.expiresAt <= now()) {
            completedResults.delete(key);
            return undefined;
        }
        return entry;
    };

    const setFreshValue = (key, value, { ttlMs } = {}) => {
        const ttl = Number(ttlMs);
        const expiresAt = Number.isFinite(ttl) && ttl > 0
            ? now() + ttl
            : Number.POSITIVE_INFINITY;
        completedResults.set(key, { value, expiresAt });
        return value;
    };

    const invalidateKey = (key) => {
        const normalizedKey = toKey(key);
        if (!normalizedKey) {
            return 0;
        }
        const hadEntry = completedResults.delete(normalizedKey);
        const hadInFlight = inFlightLoads.delete(normalizedKey);
        bumpKeyVersion(normalizedKey);
        return hadEntry || hadInFlight ? 1 : 0;
    };

    const purgeExpired = () => {
        Array.from(completedResults.keys()).forEach(purgeExpiredKey);
    };

    return {
        get(key) {
            const normalizedKey = toKey(key);
            if (!normalizedKey) {
                return undefined;
            }
            return purgeExpiredKey(normalizedKey)?.value;
        },

        set(key, value, options = {}) {
            const normalizedKey = toKey(key);
            if (!normalizedKey) {
                return value;
            }
            return setFreshValue(normalizedKey, value, options);
        },

        getOrLoad(key, loader, options = {}) {
            const normalizedKey = toKey(key);
            if (typeof loader !== 'function') {
                return Promise.resolve(undefined);
            }
            if (!normalizedKey) {
                return Promise.resolve().then(() => loader());
            }

            const cachedEntry = purgeExpiredKey(normalizedKey);
            if (cachedEntry) {
                return Promise.resolve(cachedEntry.value);
            }

            const existingLoad = inFlightLoads.get(normalizedKey);
            if (existingLoad) {
                return existingLoad;
            }

            const loadKeyVersion = getKeyVersion(normalizedKey);
            const loadGlobalVersion = globalVersion;
            let loaderPromise;
            try {
                loaderPromise = Promise.resolve(loader());
            } catch (err) {
                return Promise.reject(err);
            }

            const loadPromise = loaderPromise
                .then((value) => {
                    const invalidatedDuringLoad =
                        getKeyVersion(normalizedKey) !== loadKeyVersion ||
                        globalVersion !== loadGlobalVersion;
                    if (!invalidatedDuringLoad) {
                        setFreshValue(normalizedKey, value, options);
                    }
                    return value;
                })
                .finally(() => {
                    if (inFlightLoads.get(normalizedKey) === loadPromise) {
                        inFlightLoads.delete(normalizedKey);
                    }
                });

            inFlightLoads.set(normalizedKey, loadPromise);
            return loadPromise;
        },

        invalidate(keyOrPredicate) {
            if (typeof keyOrPredicate === 'function') {
                const keys = new Set([
                    ...completedResults.keys(),
                    ...inFlightLoads.keys(),
                ]);
                let count = 0;
                keys.forEach((key) => {
                    if (keyOrPredicate(key)) {
                        count += invalidateKey(key);
                    }
                });
                return count;
            }
            return invalidateKey(keyOrPredicate);
        },

        clear() {
            completedResults.clear();
            inFlightLoads.clear();
            keyVersions.clear();
            globalVersion += 1;
        },

        size() {
            purgeExpired();
            return completedResults.size;
        },
    };
}
